<template>
  <div class="customise-route-style">
    <!-- Route type tabs -->
    <div class="route-type-tabs">
      <button
        v-for="tab in routeTabs"
        :key="tab.key"
        class="route-type-tab"
        :class="{ 'route-type-tab--active': activeTab === tab.key }"
        @click="activeTab = tab.key as RouteTab"
      >
        {{ tab.label }}
      </button>
    </div>

    <LineStyleEditor
      :color="currentPrefs.color"
      :thickness="currentPrefs.thickness"
      :opacity="currentPrefs.opacity"
      @update:color="setColor"
      @update:thickness="setThickness"
      @update:opacity="setOpacity"
      @reset="resetToDefault"
    />
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import emitter from '../../../utils/bus';
import LineStyleEditor from './LineStyleEditor.vue';
import {
  ROUTE_STYLE_STORAGE_KEY,
  DEFAULT_ROUTE_STYLE_SETTINGS,
  getRouteStyleSettings,
  RouteStyleSettings,
  RouteTypePrefs,
} from '../../../utils/routeStyleSettings';

export type RouteTab = keyof Omit<RouteStyleSettings, 'hideMileMarkers'>;

export default defineComponent({
  name: 'CustomiseRouteStyleView',

  components: { LineStyleEditor },

  data() {
    return {
      activeTab: 'main' as RouteTab,
      settings: getRouteStyleSettings(),
      routeTabs: [
        { key: 'main',      label: 'Main'      },
        { key: 'alt',       label: 'Alt'       },
        { key: 'spur',      label: 'Spur'      },
        { key: 'recording', label: 'Recording' },
      ],
      onCloudHydrate: (() => {}) as (() => void),
    };
  },

  computed: {
    currentPrefs(): RouteTypePrefs {
      return this.settings[this.activeTab];
    },
  },

  // Refresh local state whenever the cloud-sync bridge hydrates localStorage,
  // so a value pushed from Android/iOS shows up in this dialog without a
  // page reload.
  created() {
    this.onCloudHydrate = () => { this.settings = getRouteStyleSettings(); };
    emitter.on('route-style-settings:changed', this.onCloudHydrate);
  },
  beforeUnmount() {
    if (this.onCloudHydrate) {
      emitter.off('route-style-settings:changed', this.onCloudHydrate);
    }
  },

  methods: {
    setColor(color: string) {
      this.settings[this.activeTab].color = color;
      this.persist();
    },
    setThickness(value: number) {
      this.settings[this.activeTab].thickness = value;
      this.persist();
    },
    setOpacity(value: number) {
      this.settings[this.activeTab].opacity = value;
      this.persist();
    },
    resetToDefault() {
      this.settings = {
        main:      { ...DEFAULT_ROUTE_STYLE_SETTINGS.main      },
        alt:       { ...DEFAULT_ROUTE_STYLE_SETTINGS.alt       },
        spur:      { ...DEFAULT_ROUTE_STYLE_SETTINGS.spur      },
        recording: { ...DEFAULT_ROUTE_STYLE_SETTINGS.recording },
        hideMileMarkers: false,
      };
      this.persist();
    },
    persist() {
      try {
        localStorage.setItem(ROUTE_STYLE_STORAGE_KEY, JSON.stringify(this.settings));
      } catch {}
      emitter.emit('route-style-settings:changed', { ...this.settings });
    },
  },
});
</script>

<style scoped>
.customise-route-style {
  padding: 16px;
  padding-bottom: 24px;
}

.route-type-tabs {
  display: flex;
  background: var(--gray);
  border-radius: 9999px;
  padding: 4px;
  gap: 4px;
  margin-bottom: 20px;
}

.route-type-tab {
  flex: 1;
  border: none;
  background: transparent;
  color: #aaa;
  border-radius: 9999px;
  padding: 7px 0;
  font-size: 0.82rem;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.2s, color 0.2s;
}

.route-type-tab--active {
  background: #fddf00;
  color: #17181d;
}
</style>
